'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoxPrices2026PageEN() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/en/blog/botox-prices-2026';
  const pageTitle = 'Botox Prices 2026 - How Much, What Does It Depend On?';
  const pageDescription = 'Complete guide to botox prices in 2026. Factors affecting botox costs, detailed price list and what you need to know before getting botox treatment.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Home', href: '/en' },
              { label: 'Blog', href: '/en/blog' },
              { label: 'Botox Prices 2026', href: '/en/blog/botox-prices-2026' },
            ]}
          />
        </div>

        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botox prices 2026 guide and treatment information"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Aesthetic Treatments
              </span>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botox Prices 2026 - How Much, What Does It Depend On?
            </h1>

            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Baris Cin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>January 15, 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 minutes read</span>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Share:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                One of the most curious questions of those thinking about getting botox: "How much are botox prices in 2026?" In this article, we will discuss current prices, application process and effect in detail.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                What is Botox?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Botox is a treatment method obtained from botulinum toxin used to reduce wrinkles and fine lines caused by muscle movements. This FDA-approved method temporarily relaxes facial muscles, providing a younger and more dynamic appearance.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Benefits of Botox Treatment:
              </h3>
              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <ul className="space-y-2 text-gray-800">
                  <li>• Reduces expression lines and wrinkles</li>
                  <li>• Provides natural facial expressions</li>
                  <li>• Quick and painless application</li>
                  <li>• No recovery period required</li>
                  <li>• Long-lasting effects (4-6 months)</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botox Application Process
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botox application is a simple procedure that takes 10-15 minutes. The process consists of the following steps:
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-8">
                <ol className="space-y-3 text-gray-800">
                  <li className="flex items-start">
                    <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-1">1</span>
                    <div>
                      <strong>Consultation:</strong> Your doctor examines your face and determines the appropriate treatment areas.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-1">2</span>
                    <div>
                      <strong>Preparation:</strong> The treatment area is cleaned and a topical anesthetic can be applied if necessary.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-1">3</span>
                    <div>
                      <strong>Injection:</strong> Botox is injected into specific muscle points with fine needles.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-1">4</span>
                    <div>
                      <strong>Completion:</strong> The application is completed and you can return to your daily activities immediately.
                    </div>
                  </li>
                </ol>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                How Long Does Botox Effect Last?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Botox effects typically last 4-6 months. The effect starts to appear within 3-5 days and reaches its peak after 2 weeks. The duration of effect may vary depending on factors such as age, skin structure, muscle strength and lifestyle.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Botox Prices 2026 - Current Price List
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Botox prices vary depending on the treatment area, the amount of product used and the clinic's location. Here are the current botox prices for 2026:
              </p>

              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Treatment Area
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Price Range (USD)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Average Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Forehead Lines</td>
                      <td className="px-6 py-4 text-sm text-gray-900">$120 - $180</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4-6 months</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Between Eyebrows (Frown Lines)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">$100 - $160</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4-6 months</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Around Eyes (Crow's Feet)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">$100 - $160</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3-5 months</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Three Area Combo</td>
                      <td className="px-6 py-4 text-sm text-gray-900">$280 - $380</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4-6 months</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Excessive Sweating Treatment</td>
                      <td className="px-6 py-4 text-sm text-gray-900">$300 - $480</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6-8 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg mb-8">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Note:</strong> Prices may vary depending on the clinic, doctor's experience and treatment area. It is recommended to get a consultation for accurate pricing.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Factors Affecting Botox Prices
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4">Treatment Related Factors:</h3>
                  <ul className="space-y-2 text-blue-700">
                    <li>• Treatment area and size</li>
                    <li>• Amount of botox to be used</li>
                    <li>• Number of injection points</li>
                    <li>• Frequency of treatment</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-purple-800 mb-4">Clinic Related Factors:</h3>
                  <ul className="space-y-2 text-purple-700">
                    <li>• Doctor's experience and expertise</li>
                    <li>• Clinic location and prestige</li>
                    <li>• Quality of botox brand used</li>
                    <li>• Additional services offered</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Pre-Treatment Considerations
              </h2>
              
              <div className="bg-red-50 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-red-800 mb-4">Important Points to Consider:</h3>
                <ul className="space-y-3 text-red-700">
                  <li>• Avoid alcohol consumption 24 hours before treatment</li>
                  <li>• Stop blood thinners if using (consult your doctor)</li>
                  <li>• Do not apply makeup on the treatment day</li>
                  <li>• Inform about pregnancy or breastfeeding</li>
                  <li>• Share your medical history and medications used</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    1. Is botox application painful?
                  </h3>
                  <p className="text-gray-700">
                    Botox application is generally painless. Fine needles are used and you may feel only a slight pinch sensation.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    2. When do the effects of botox start to show?
                  </h3>
                  <p className="text-gray-700">
                    The effects start to appear within 3-5 days and reach their peak after 2 weeks.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    3. How often should botox be repeated?
                  </h3>
                  <p className="text-gray-700">
                    Botox application is typically repeated every 4-6 months. Regular applications can extend the duration of effect.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    4. Are there any side effects of botox?
                  </h3>
                  <p className="text-gray-700">
                    When applied by an expert doctor, botox is very safe. Temporary side effects such as slight redness or swelling may occur.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">
                  Ready for Professional Botox Treatment?
                </h3>
                <p className="text-lg mb-6 opacity-90">
                  Get a free consultation with Op. Dr. Baris Cin to determine the most suitable treatment plan for you.
                </p>
                <a
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Hello, I would like to get information about botox prices and treatment.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-200"
                >
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.690z"/>
                  </svg>
                  WhatsApp Appointment
                </a>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">References</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• American Board of Cosmetic Surgery - <a href="https://www.americanboardcosmeticsurgery.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.americanboardcosmeticsurgery.org</a></li>
                    <li>• Turkish Society of Aesthetic and Plastic Surgery - Clinical Guidelines</li>
                    <li>• 2024-2026 Aesthetic Treatment Data (Evaluation with 15 clinics across Turkey)</li>
                    <li>• FDA Botox Treatment Guidelines and Safety Reports</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
